import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Bus, MapPin, Play, Square, Navigation, Clock, User, Plus,
  Trash2, Edit, Route, CircleDot, CheckCircle2, Loader2, AlertCircle,
  History, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ LIVE MAP COMPONENT ============
const LiveMap = ({ trips, lines, selectedLine }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const mapInitialized = useRef(false);

  useEffect(() => {
    if (!mapRef.current || mapInitialized.current) return;

    const loadLeaflet = async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');

      // Check if container already has a map
      if (mapRef.current._leaflet_id) {
        return;
      }

      mapInitialized.current = true;
      const map = L.map(mapRef.current).setView([-4.3217, 15.3125], 13); // Kinshasa default
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 18,
      }).addTo(map);

      mapInstance.current = map;
    };
    loadLeaflet();

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        mapInitialized.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstance.current) return;

    const loadMarkers = async () => {
      const L = (await import('leaflet')).default;
      const map = mapInstance.current;

      // Clear old markers
      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];

      // Add bus markers for active trips
      trips.forEach(trip => {
        if (trip.current_position) {
          const busIcon = L.divIcon({
            className: 'bus-marker',
            html: `<div style="background:#0F2C59;color:#FFD700;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid #FFD700;box-shadow:0 2px 8px rgba(0,0,0,.3);">&#128652;</div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
          });
          const marker = L.marker(
            [trip.current_position.lat, trip.current_position.lng],
            { icon: busIcon }
          ).addTo(map);
          marker.bindPopup(`<b>${trip.line_name}</b><br/>Chauffeur: ${trip.driver_name}`);
          markersRef.current.push(marker);
        }
      });

      // Add stop markers for selected line
      const lineToShow = selectedLine || (lines.length > 0 ? lines[0] : null);
      if (lineToShow?.stops) {
        lineToShow.stops.forEach((stop, i) => {
          if (stop.lat && stop.lng) {
            const stopIcon = L.divIcon({
              className: 'stop-marker',
              html: `<div style="background:#fff;color:#0F2C59;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #0F2C59;">${i + 1}</div>`,
              iconSize: [24, 24],
              iconAnchor: [12, 12],
            });
            const m = L.marker([stop.lat, stop.lng], { icon: stopIcon }).addTo(map);
            m.bindPopup(`<b>Arrêt ${i + 1}:</b> ${stop.name}`);
            markersRef.current.push(m);
          }
        });
      }

      // Fit bounds if we have markers
      if (markersRef.current.length > 0) {
        const group = L.featureGroup(markersRef.current);
        map.fitBounds(group.getBounds().pad(0.2));
      }
    };
    loadMarkers();
  }, [trips, lines, selectedLine]);

  return (
    <div ref={mapRef} className="w-full h-[400px] rounded-lg border" data-testid="transport-map" />
  );
};

// ============ ADMIN VIEW ============
const AdminTransportView = () => {
  const [lines, setLines] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLineDialog, setShowLineDialog] = useState(false);
  const [showBusDialog, setShowBusDialog] = useState(false);
  const [editLine, setEditLine] = useState(null);
  const [lineForm, setLineForm] = useState({ name: '', description: '', stops: [] });
  const [busForm, setBusForm] = useState({ line_id: '', plate_number: '', capacity: 50, driver_id: '' });
  const [newStop, setNewStop] = useState({ name: '', lat: '', lng: '' });
  const [activeTrips, setActiveTrips] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [linesRes, busesRes, driversRes, tripsRes] = await Promise.all([
        axios.get(`${API}/api/transport/lines`),
        axios.get(`${API}/api/transport/buses`),
        axios.get(`${API}/api/transport/drivers`),
        axios.get(`${API}/api/transport/trips/active`),
      ]);
      setLines(Array.isArray(linesRes.data) ? linesRes.data : []);
      setBuses(Array.isArray(busesRes.data) ? busesRes.data : []);
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
      setActiveTrips(Array.isArray(tripsRes.data) ? tripsRes.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addStop = () => {
    if (!newStop.name) return;
    setLineForm(prev => ({
      ...prev,
      stops: [...prev.stops, {
        name: newStop.name,
        order: prev.stops.length + 1,
        lat: newStop.lat ? parseFloat(newStop.lat) : null,
        lng: newStop.lng ? parseFloat(newStop.lng) : null
      }]
    }));
    setNewStop({ name: '', lat: '', lng: '' });
  };

  const removeStop = (idx) => {
    setLineForm(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  const saveLine = async () => {
    if (!lineForm.name) { toast.error('Nom de ligne requis'); return; }
    try {
      if (editLine) {
        await axios.put(`${API}/api/transport/lines/${editLine.id}`, lineForm);
        toast.success('Ligne mise à jour');
      } else {
        await axios.post(`${API}/api/transport/lines`, lineForm);
        toast.success('Ligne créée');
      }
      setShowLineDialog(false);
      setEditLine(null);
      setLineForm({ name: '', description: '', stops: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteLine = async (id) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await axios.delete(`${API}/api/transport/lines/${id}`);
      toast.success('Ligne supprimée');
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const saveBus = async () => {
    if (!busForm.plate_number || !busForm.line_id) { toast.error('Plaque et ligne requises'); return; }
    try {
      await axios.post(`${API}/api/transport/buses`, busForm);
      toast.success('Bus créé');
      setShowBusDialog(false);
      setBusForm({ line_id: '', plate_number: '', capacity: 50, driver_id: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteBus = async (id) => {
    if (!window.confirm('Supprimer ce bus ?')) return;
    try {
      await axios.delete(`${API}/api/transport/buses/${id}`);
      toast.success('Bus supprimé');
      fetchData();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <Route className="w-6 h-6 mx-auto mb-1 text-blue-600" />
          <p className="text-2xl font-bold" data-testid="lines-count">{lines.length}</p>
          <p className="text-xs text-gray-500">Lignes</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Bus className="w-6 h-6 mx-auto mb-1 text-emerald-600" />
          <p className="text-2xl font-bold" data-testid="buses-count">{buses.length}</p>
          <p className="text-xs text-gray-500">Bus</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <User className="w-6 h-6 mx-auto mb-1 text-purple-600" />
          <p className="text-2xl font-bold" data-testid="drivers-count">{drivers.length}</p>
          <p className="text-xs text-gray-500">Chauffeurs</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Navigation className="w-6 h-6 mx-auto mb-1 text-orange-600" />
          <p className="text-2xl font-bold" data-testid="active-trips-count">{activeTrips.length}</p>
          <p className="text-xs text-gray-500">Trajets actifs</p>
        </CardContent></Card>
      </div>

      {/* Map */}
      {activeTrips.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Navigation className="w-4 h-4" /> Suivi en temps réel</CardTitle></CardHeader>
          <CardContent>
            <LiveMap trips={activeTrips} lines={lines} />
          </CardContent>
        </Card>
      )}

      {/* Lines */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Route className="w-4 h-4" /> Lignes de bus</CardTitle>
          <Button size="sm" className="gap-1" onClick={() => { setEditLine(null); setLineForm({ name: '', description: '', stops: [] }); setShowLineDialog(true); }} data-testid="add-line-btn">
            <Plus className="w-3.5 h-3.5" /> Nouvelle ligne
          </Button>
        </CardHeader>
        <CardContent>
          {lines.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Aucune ligne. Créez votre première ligne de bus.</p>
          ) : (
            <div className="space-y-3">
              {lines.map(line => (
                <div key={line.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50" data-testid={`line-${line.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{line.name}</span>
                      <Badge variant="outline" className="text-xs">{line.stops?.length || 0} arrêts</Badge>
                      <Badge variant="outline" className="text-xs">{line.bus_count} bus</Badge>
                      {line.has_active_trip && <Badge className="bg-green-100 text-green-700 text-xs">En route</Badge>}
                    </div>
                    {line.description && <p className="text-xs text-gray-500 mt-0.5">{line.description}</p>}
                    {line.stops?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {line.stops.map((s, i) => (
                          <React.Fragment key={i}>
                            <span className="text-xs text-gray-600">{s.name}</span>
                            {i < line.stops.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button size="icon" variant="ghost" className="w-8 h-8" onClick={() => {
                      setEditLine(line);
                      setLineForm({ name: line.name, description: line.description || '', stops: line.stops || [] });
                      setShowLineDialog(true);
                    }} data-testid={`edit-line-${line.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="w-8 h-8 text-red-500" onClick={() => deleteLine(line.id)} data-testid={`delete-line-${line.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Buses */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Bus className="w-4 h-4" /> Bus</CardTitle>
          <Button size="sm" className="gap-1" onClick={() => setShowBusDialog(true)} data-testid="add-bus-btn" disabled={lines.length === 0}>
            <Plus className="w-3.5 h-3.5" /> Nouveau bus
          </Button>
        </CardHeader>
        <CardContent>
          {buses.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Aucun bus enregistré.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {buses.map(bus => (
                <div key={bus.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`bus-${bus.id}`}>
                  <div>
                    <p className="font-medium">{bus.plate_number}</p>
                    <p className="text-xs text-gray-500">Ligne: {bus.line_name} | {bus.capacity} places</p>
                    <p className="text-xs text-gray-500">Chauffeur: {bus.driver_name}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="w-8 h-8 text-red-500" onClick={() => deleteBus(bus.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drivers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" /> Chauffeurs inscrits</CardTitle>
        </CardHeader>
        <CardContent>
          {drivers.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Aucun chauffeur inscrit. Les chauffeurs peuvent créer un compte avec le rôle "Chauffeur".</p>
          ) : (
            <div className="space-y-2">
              {drivers.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`driver-${d.id}`}>
                  <div>
                    <p className="font-medium">{d.first_name} {d.last_name}</p>
                    <p className="text-xs text-gray-500">{d.email} | {d.phone || 'Pas de tel.'}</p>
                  </div>
                  <div className="text-right text-xs">
                    {d.assigned_bus ? (
                      <Badge className="bg-blue-50 text-blue-700">{d.assigned_bus} - {d.assigned_line}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-400">Non assigné</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Line Dialog */}
      <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editLine ? 'Modifier la ligne' : 'Nouvelle ligne de bus'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de la ligne *</Label>
              <Input value={lineForm.name} onChange={e => setLineForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Ligne 1 - Gombe-Limete" data-testid="line-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={lineForm.description} onChange={e => setLineForm(p => ({ ...p, description: e.target.value }))} placeholder="Description de la ligne" />
            </div>

            {/* Stops */}
            <div>
              <Label className="mb-2 block">Arrêts ({lineForm.stops.length})</Label>
              {lineForm.stops.length > 0 && (
                <div className="space-y-1 mb-3">
                  {lineForm.stops.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                      <CircleDot className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <span className="flex-1">{i + 1}. {s.name}</span>
                      {s.lat && <span className="text-xs text-gray-400">{s.lat?.toFixed(4)}, {s.lng?.toFixed(4)}</span>}
                      <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => removeStop(i)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input placeholder="Nom de l'arrêt" value={newStop.name} onChange={e => setNewStop(p => ({ ...p, name: e.target.value }))} className="flex-1" data-testid="stop-name-input" />
                <Input placeholder="Lat" value={newStop.lat} onChange={e => setNewStop(p => ({ ...p, lat: e.target.value }))} className="w-20" type="number" step="any" />
                <Input placeholder="Lng" value={newStop.lng} onChange={e => setNewStop(p => ({ ...p, lng: e.target.value }))} className="w-20" type="number" step="any" />
                <Button size="sm" variant="outline" onClick={addStop} data-testid="add-stop-btn"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>

            <Button onClick={saveLine} className="w-full" data-testid="save-line-btn">{editLine ? 'Mettre à jour' : 'Créer la ligne'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bus Dialog */}
      <Dialog open={showBusDialog} onOpenChange={setShowBusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau bus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Plaque d'immatriculation *</Label>
              <Input value={busForm.plate_number} onChange={e => setBusForm(p => ({ ...p, plate_number: e.target.value }))} placeholder="Ex: KN-1234-AB" data-testid="bus-plate-input" />
            </div>
            <div>
              <Label>Ligne</Label>
              <Select value={busForm.line_id} onValueChange={v => setBusForm(p => ({ ...p, line_id: v }))}>
                <SelectTrigger data-testid="bus-line-select"><SelectValue placeholder="Sélectionner une ligne" /></SelectTrigger>
                <SelectContent>
                  {lines.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Capacité</Label>
              <Input type="number" value={busForm.capacity} onChange={e => setBusForm(p => ({ ...p, capacity: parseInt(e.target.value) || 50 }))} />
            </div>
            <div>
              <Label>Chauffeur</Label>
              <Select value={busForm.driver_id} onValueChange={v => setBusForm(p => ({ ...p, driver_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Assigner un chauffeur (optionnel)" /></SelectTrigger>
                <SelectContent>
                  {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveBus} className="w-full" data-testid="save-bus-btn">Créer le bus</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ DRIVER VIEW ============
const DriverTransportView = () => {
  const [driverInfo, setDriverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [gpsWatchId, setGpsWatchId] = useState(null);
  const [currentPos, setCurrentPos] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState('');
  const positionIntervalRef = useRef(null);

  const fetchInfo = useCallback(async () => {
    try {
      const [infoRes, linesRes] = await Promise.all([
        axios.get(`${API}/api/transport/driver/my-info`),
        axios.get(`${API}/api/transport/lines`),
      ]);
      setDriverInfo(infoRes.data);
      setLines(Array.isArray(linesRes.data) ? linesRes.data : []);
      if (infoRes.data?.line) setSelectedLineId(infoRes.data.line.id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInfo(); return () => { if (gpsWatchId) navigator.geolocation.clearWatch(gpsWatchId); if (positionIntervalRef.current) clearInterval(positionIntervalRef.current); }; }, [fetchInfo, gpsWatchId]);

  const startTrip = async () => {
    const lineId = selectedLineId || driverInfo?.line?.id;
    if (!lineId) { toast.error('Sélectionnez une ligne'); return; }

    setStarting(true);
    try {
      // Get initial position
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });

      const body = { line_id: lineId, bus_id: driverInfo?.bus?.id || null };
      if (pos) { body.lat = pos.lat; body.lng = pos.lng; setCurrentPos(pos); }

      const res = await axios.post(`${API}/api/transport/trips/start`, body);
      toast.success('Trajet démarré !');

      // Start GPS tracking
      const tripId = res.data.trip_id;
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const newPos = { lat: position.coords.latitude, lng: position.coords.longitude };
          setCurrentPos(newPos);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );
      setGpsWatchId(watchId);

      // Send position updates every 10 seconds
      positionIntervalRef.current = setInterval(async () => {
        try {
          const p = await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              () => resolve(null),
              { enableHighAccuracy: true, timeout: 8000 }
            );
          });
          if (p) {
            await axios.put(`${API}/api/transport/trips/${tripId}/position`, p);
          }
        } catch {}
      }, 10000);

      fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur au démarrage');
    } finally {
      setStarting(false);
    }
  };

  const endTrip = async () => {
    if (!driverInfo?.active_trip) return;
    setEnding(true);
    try {
      await axios.put(`${API}/api/transport/trips/${driverInfo.active_trip.id}/end`);
      toast.success('Trajet terminé');

      // Stop tracking
      if (gpsWatchId) { navigator.geolocation.clearWatch(gpsWatchId); setGpsWatchId(null); }
      if (positionIntervalRef.current) { clearInterval(positionIntervalRef.current); positionIntervalRef.current = null; }

      fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setEnding(false);
    }
  };

  const markStop = async (stop) => {
    if (!driverInfo?.active_trip) return;
    try {
      await axios.put(`${API}/api/transport/trips/${driverInfo.active_trip.id}/stop-reached`, {
        stop_name: stop.name,
        stop_order: stop.order
      });
      toast.success(`Arrêt "${stop.name}" atteint`);
      fetchInfo();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  const activeTrip = driverInfo?.active_trip;
  const activeLine = activeTrip
    ? lines.find(l => l.id === activeTrip.line_id)
    : (driverInfo?.line || lines.find(l => l.id === selectedLineId));

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <Card className={activeTrip ? 'border-green-300 bg-green-50/30' : ''}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold" data-testid="driver-status">
                {activeTrip ? 'Trajet en cours' : 'Prêt à démarrer'}
              </h2>
              {driverInfo?.bus && (
                <p className="text-sm text-gray-500">Bus: {driverInfo.bus.plate_number}</p>
              )}
              {activeTrip && (
                <p className="text-sm text-gray-500 mt-1">
                  Ligne: {activeTrip.line_name} | Démarré: {new Date(activeTrip.started_at).toLocaleTimeString('fr-FR')}
                </p>
              )}
              {currentPos && (
                <p className="text-xs text-gray-400 mt-1">
                  GPS: {currentPos.lat?.toFixed(5)}, {currentPos.lng?.toFixed(5)}
                </p>
              )}
            </div>
            <div>
              {activeTrip ? (
                <Button size="lg" variant="destructive" className="gap-2" onClick={endTrip} disabled={ending} data-testid="end-trip-btn">
                  {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                  Terminer le trajet
                </Button>
              ) : (
                <div className="flex flex-col gap-2 items-end">
                  {!driverInfo?.line && lines.length > 0 && (
                    <Select value={selectedLineId} onValueChange={setSelectedLineId}>
                      <SelectTrigger className="w-48" data-testid="driver-line-select"><SelectValue placeholder="Choisir la ligne" /></SelectTrigger>
                      <SelectContent>
                        {lines.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="lg" className="gap-2 bg-green-600 hover:bg-green-700" onClick={startTrip} disabled={starting || (!selectedLineId && !driverInfo?.line)} data-testid="start-trip-btn">
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    Démarrer le trajet
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stops checklist */}
      {activeLine && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Arrêts - {activeLine.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(activeLine.stops || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucun arrêt configuré pour cette ligne.</p>
            ) : (
              <div className="space-y-2">
                {(activeLine.stops || []).map((stop, i) => {
                  const passed = activeTrip?.stops_passed?.some(sp => sp.stop_order === stop.order);
                  return (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${passed ? 'bg-green-50 border-green-200' : 'bg-white'}`} data-testid={`stop-item-${i}`}>
                      {passed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <CircleDot className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={`flex-1 text-sm ${passed ? 'text-green-700 line-through' : 'text-gray-800'}`}>
                        {stop.order}. {stop.name}
                      </span>
                      {activeTrip && !passed && (
                        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => markStop(stop)} data-testid={`mark-stop-${i}`}>
                          <CheckCircle2 className="w-3 h-3" /> Atteint
                        </Button>
                      )}
                      {passed && (
                        <span className="text-xs text-green-600">
                          {activeTrip.stops_passed.find(sp => sp.stop_order === stop.order)?.reached_at
                            ? new Date(activeTrip.stops_passed.find(sp => sp.stop_order === stop.order).reached_at).toLocaleTimeString('fr-FR')
                            : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trip History */}
      <TripHistory />
    </div>
  );
};

// ============ TRIP HISTORY ============
const TripHistory = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/transport/trips/history`);
        setTrips(Array.isArray(res.data) ? res.data : []);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return null;
  if (trips.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Historique des trajets</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {trips.slice(0, 10).map(trip => (
            <div key={trip.id} className="flex items-center justify-between p-3 rounded-lg border text-sm" data-testid={`trip-history-${trip.id}`}>
              <div>
                <p className="font-medium">{trip.line_name}</p>
                <p className="text-xs text-gray-500">
                  {new Date(trip.started_at).toLocaleDateString('fr-FR')} | {new Date(trip.started_at).toLocaleTimeString('fr-FR')} - {trip.ended_at ? new Date(trip.ended_at).toLocaleTimeString('fr-FR') : ''}
                </p>
              </div>
              <Badge variant="outline" className="text-xs">{trip.stops_passed?.length || 0} arrêts</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============ STUDENT/PUBLIC VIEW ============
const PublicTransportView = () => {
  const [lines, setLines] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLine, setSelectedLine] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [linesRes, tripsRes] = await Promise.all([
        axios.get(`${API}/api/transport/lines`),
        axios.get(`${API}/api/transport/trips/active`),
      ]);
      setLines(Array.isArray(linesRes.data) ? linesRes.data : []);
      setActiveTrips(Array.isArray(tripsRes.data) ? tripsRes.data : []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Active trips map */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Navigation className="w-4 h-4" />
            Suivi des bus en temps réel
            {activeTrips.length > 0 && <Badge className="bg-green-100 text-green-700 text-xs ml-2">{activeTrips.length} en route</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LiveMap trips={activeTrips} lines={lines} selectedLine={selectedLine} />
        </CardContent>
      </Card>

      {/* Active trips list */}
      {activeTrips.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Bus en circulation</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeTrips.map(trip => (
                <div key={trip.id} className="flex items-center gap-3 p-3 rounded-lg border bg-green-50/50" data-testid={`active-trip-${trip.id}`}>
                  <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Bus className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{trip.line_name}</p>
                    <p className="text-xs text-gray-500">Chauffeur: {trip.driver_name} | Départ: {new Date(trip.started_at).toLocaleTimeString('fr-FR')}</p>
                    {trip.stops_passed?.length > 0 && (
                      <p className="text-xs text-green-600 mt-0.5">
                        Dernier arrêt: {trip.stops_passed[trip.stops_passed.length - 1]?.name}
                      </p>
                    )}
                  </div>
                  <Navigation className="w-4 h-4 text-green-600 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lines list */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Route className="w-4 h-4" /> Lignes disponibles</CardTitle></CardHeader>
        <CardContent>
          {lines.length === 0 ? (
            <p className="text-gray-500 text-center py-6">Aucune ligne de bus configurée pour votre université.</p>
          ) : (
            <div className="space-y-3">
              {lines.map(line => (
                <div key={line.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedLine?.id === line.id ? 'border-blue-300 bg-blue-50/50' : 'hover:bg-gray-50'}`}
                  onClick={() => setSelectedLine(selectedLine?.id === line.id ? null : line)}
                  data-testid={`public-line-${line.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-sm">{line.name}</span>
                    {line.has_active_trip && <Badge className="bg-green-100 text-green-700 text-xs">En route</Badge>}
                  </div>
                  {line.description && <p className="text-xs text-gray-500 mt-1">{line.description}</p>}
                  {line.stops?.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      {line.stops.map((s, i) => (
                        <React.Fragment key={i}>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{s.name}</span>
                          {i < line.stops.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ MAIN PAGE ============
export const TransportPage = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_academic', 'sg_admin', 'admin_staff'].includes(user?.role);
  const isDriver = user?.role === 'driver';

  return (
    <div className="space-y-6 animate-slide-in" data-testid="transport-page">
      <div>
        <h1 className="heading-2">Transport Universitaire</h1>
        <p className="text-gray-500 mt-1">
          {isAdmin ? 'Gérez les lignes, bus, chauffeurs et suivez les trajets' :
           isDriver ? 'Gérez vos trajets et suivez votre itinéraire' :
           'Suivez les bus de votre université en temps réel'}
        </p>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="manage" className="w-full">
          <TabsList>
            <TabsTrigger value="manage" data-testid="tab-manage">Gestion</TabsTrigger>
            <TabsTrigger value="tracking" data-testid="tab-tracking">Suivi GPS</TabsTrigger>
          </TabsList>
          <TabsContent value="manage"><AdminTransportView /></TabsContent>
          <TabsContent value="tracking"><PublicTransportView /></TabsContent>
        </Tabs>
      ) : isDriver ? (
        <DriverTransportView />
      ) : (
        <PublicTransportView />
      )}
    </div>
  );
};

export default TransportPage;
